import { Users, UserPlus, Sparkles, Clock, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { computeCustomerOverviewStats } from "@/lib/customer-overview-stats";

type Props = {
  customers: Parameters<typeof computeCustomerOverviewStats>[0];
  loading?: boolean;
  statLabel?: "appointments" | "visits" | "orders";
  customerLabel?: string;
};

export function CustomerOverviewCards({
  customers,
  loading = false,
  statLabel = "appointments",
  customerLabel = "Customers",
}: Props) {
  const stats = computeCustomerOverviewStats(customers);
  const vipLabel =
    statLabel === "visits"
      ? "VIP (5+ visits)"
      : statLabel === "orders"
        ? "VIP (5+ orders)"
        : "VIP (5+ bookings)";
  const noRecentLabel =
    statLabel === "visits"
      ? "No visit (30 days)"
      : statLabel === "orders"
        ? "No order (30 days)"
        : "No booking (30 days)";

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-lg sm:text-2xl font-bold">
                {loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : stats.total}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Total {customerLabel}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
              <UserPlus className="h-4 w-4 sm:h-5 sm:w-5 text-success" />
            </div>
            <div className="min-w-0">
              <p className="text-lg sm:text-2xl font-bold">
                {loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : stats.new30}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">New (30 days)</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-warning" />
            </div>
            <div className="min-w-0">
              <p className="text-lg sm:text-2xl font-bold">
                {loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : stats.vip}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{vipLabel}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-secondary-foreground" />
            </div>
            <div className="min-w-0">
              <p className="text-lg sm:text-2xl font-bold">
                {loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : stats.noBooking30}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{noRecentLabel}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
